import {useState} from 'react'
import {useEntityMutation} from '@/hooks/useEntityMutation'
import {useDialog} from '@/hooks/useDialog'
import {Check, Edit, Settings, X} from 'lucide-react'
import {Button} from '@/components/ui/button'
import {Card, CardContent, CardDescription, CardTitle} from '@/components/ui/card'
import {RowCardHeader} from '@/components/shared/RowCardHeader'
import {Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {SaveCancelFooter} from '@/components/shared/SaveCancelFooter'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select'
import {Switch} from '@/components/ui/switch'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {Strong} from '@/components/shared/Strong'
import {MutedText} from '@/components/shared/MutedText'
import {FormGrid} from '@/components/shared/FormGrid'
import {Stack} from '@/components/shared/Stack'
import {DetailGrid} from '@/components/shared/DetailGrid'
import {InlineRow} from '@/components/shared/InlineRow'
import {Icon} from '@/components/shared/Icon'
import {CodeText} from '@/components/shared/CodeText'
import {AuthType, ScimOutboundConfigResponseFields} from '@/types/outbound-connector'
import type {
    OutboundConnector,
    ScimAuthConfig,
    ScimOutboundConfigCreateRequest,
    ScimOutboundConfigUpdateRequest,
} from '@/types/outbound-connector'
import {ConnectorConfigForm} from './ConnectorConfigForm'
import type {ConnectorConfigFormState} from './ConnectorConfigForm'

 
const PATCH_STYLES = [
    {value: 'RFC7644', labelKey: 'rfc7644' as const},
    {value: 'SALESFORCE', labelKey: 'salesforce' as const},
    {value: 'MICROSOFT', labelKey: 'microsoft' as const},
]
 

const DEFAULT_SCIM_ID_ATTRIBUTE = 'scim_id'

interface ScimFormState extends ConnectorConfigFormState {
    baseUrl: string
    userEndpoint: string
    groupEndpoint: string
    userIdAttribute: string
    groupIdAttribute: string
    supportsBulk: boolean
    supportsEtag: boolean
    patchStyle: string
    version: number
}

const DEFAULT_SCIM_FORM: ScimFormState = {
    baseUrl: '',
    userEndpoint: '/Users',
    groupEndpoint: '/Groups',
    userIdAttribute: DEFAULT_SCIM_ID_ATTRIBUTE,
    groupIdAttribute: DEFAULT_SCIM_ID_ATTRIBUTE,
    authType: AuthType.BEARER_TOKEN,
    tokenSecretRef: '',
    tokenUrl: '',
    clientId: '',
    clientSecretRef: '',
    scope: '',
    username: '',
    passwordSecretRef: '',
    connectTimeoutMs: 5000,
    readTimeoutMs: 30000,
    maxRetries: 3,
    retryBackoffMs: 1000,
    supportsBulk: false,
    supportsEtag: true,
    patchStyle: 'RFC7644',
    version: 0,
}

interface ScimConfigCardProps {
    connector: OutboundConnector
    canUpdate: boolean
}

export function ScimConfigCard({connector, canUpdate}: ScimConfigCardProps) {
    const services = useServices()
    const i18n = useI18n()
    const createDialog = useDialog()
    const editDialog = useDialog()
    const [form, setForm] = useState<ScimFormState>(DEFAULT_SCIM_FORM)

    const updateForm = (updates: Partial<ScimFormState>) => setForm((prev) => ({...prev, ...updates}))

    const createMutation = useEntityMutation({
        mutationFn: (data: ScimOutboundConfigCreateRequest) =>
            services.outboundConnectors.createScimConfig(connector.id, data),
        invalidate: ['connector', connector.id],
        successMessage: i18n.connectors.configurationSaved,
        closeDialog: createDialog.close,
    })

    const updateMutation = useEntityMutation({
        mutationFn: (data: ScimOutboundConfigUpdateRequest) =>
            services.outboundConnectors.updateScimConfig(connector.id, data),
        invalidate: ['connector', connector.id],
        successMessage: i18n.connectors.configurationSaved,
        closeDialog: editDialog.close,
    })

    const openCreate = () => {
        setForm(DEFAULT_SCIM_FORM)
        createDialog.open()
    }

    const openEdit = () => {
        const config = connector.scimConfig
        if (!config) return
        setForm({
            baseUrl: config.baseUrl,
            userEndpoint: config.userEndpoint,
            groupEndpoint: config.groupEndpoint,
            userIdAttribute: config.userIdAttribute,
            groupIdAttribute: config.groupIdAttribute,
            authType: config.authType,
            tokenSecretRef: '',
            tokenUrl: config.tokenUrl ?? '',
            clientId: config.clientId ?? '',
            clientSecretRef: '',
            scope: config.scope ?? '',
            username: config.username ?? '',
            passwordSecretRef: '',
            connectTimeoutMs: config.connectTimeoutMs,
            readTimeoutMs: config.readTimeoutMs,
            maxRetries: config.maxRetries,
            retryBackoffMs: config.retryBackoffMs,
            supportsBulk: config.supportsBulk,
            supportsEtag: config.supportsEtag,
            patchStyle: config.patchStyle,
            version: config.version,
        })
        editDialog.open()
    }

    const buildAuthConfig = (): ScimAuthConfig => {
        const auth: ScimAuthConfig = {authType: form.authType}
        if (form.authType === AuthType.BEARER_TOKEN && form.tokenSecretRef) {
            auth.tokenSecretRef = form.tokenSecretRef
        } else if (form.authType === AuthType.OAUTH2_CLIENT) {
            auth.tokenUrl = form.tokenUrl
            auth.clientId = form.clientId
            if (form.clientSecretRef) auth.clientSecretRef = form.clientSecretRef
            auth.scope = form.scope
        } else if (form.authType === AuthType.BASIC_AUTH) {
            auth.username = form.username
            if (form.passwordSecretRef) auth.passwordSecretRef = form.passwordSecretRef
        }
        return auth
    }

    const handleCreate = () => {
        const auth: ScimAuthConfig = buildAuthConfig()
        if (form.authType === AuthType.BEARER_TOKEN) auth.tokenSecretRef = form.tokenSecretRef

        const request: ScimOutboundConfigCreateRequest = {
            baseUrl: form.baseUrl,
            userEndpoint: form.userEndpoint,
            groupEndpoint: form.groupEndpoint,
            userIdAttribute: form.userIdAttribute,
            groupIdAttribute: form.groupIdAttribute,
            auth,
            http: {
                connectTimeoutMs: form.connectTimeoutMs,
                readTimeoutMs: form.readTimeoutMs,
                maxRetries: form.maxRetries,
                retryBackoffMs: form.retryBackoffMs,
            },
            supportsBulk: form.supportsBulk,
            supportsEtag: form.supportsEtag,
            patchStyle: form.patchStyle,
        }
        createMutation.mutate(request)
    }

    const handleUpdate = () => {
        const request: ScimOutboundConfigUpdateRequest = {
            baseUrl: form.baseUrl,
            userEndpoint: form.userEndpoint,
            groupEndpoint: form.groupEndpoint,
            userIdAttribute: form.userIdAttribute,
            groupIdAttribute: form.groupIdAttribute,
            auth: buildAuthConfig(),
            http: {
                connectTimeoutMs: form.connectTimeoutMs,
                readTimeoutMs: form.readTimeoutMs,
                maxRetries: form.maxRetries,
                retryBackoffMs: form.retryBackoffMs,
            },
            supportsBulk: form.supportsBulk,
            supportsEtag: form.supportsEtag,
            patchStyle: form.patchStyle,
            version: form.version,
        }
        updateMutation.mutate(request)
    }

    const scimConfig = connector.scimConfig

    const dialogContent = (mode: 'create' | 'edit') => {
        const handleSubmit = () => {
            if (mode === 'create') {
                handleCreate()
            } else {
                handleUpdate()
            }
        }
        return (
        <form onSubmit={(e) => { e.preventDefault(); handleSubmit() }}>
            <div className="grid gap-6 py-4">
                {/* Endpoint Section */}
                <Stack>
                    <Strong>{i18n.connectors.endpoint}</Strong>
                    <FormGrid>
                        <FormGrid>
                            <Label htmlFor={ScimOutboundConfigResponseFields.baseUrl}>{i18n.connectors.baseUrl}</Label>
                            <Input
                                id={ScimOutboundConfigResponseFields.baseUrl}
                                value={form.baseUrl}
                                onChange={(e) => updateForm({baseUrl: e.target.value})}
                                placeholder={i18n.connectors.scimBaseUrlPlaceholder}
                            />
                        </FormGrid>
                        <DetailGrid columns={2} responsive={false}>
                            <FormGrid>
                                <Label htmlFor={ScimOutboundConfigResponseFields.userEndpoint}>{i18n.connectors.userEndpoint}</Label>
                                <Input
                                    id={ScimOutboundConfigResponseFields.userEndpoint}
                                    value={form.userEndpoint}
                                    onChange={(e) => updateForm({userEndpoint: e.target.value})}
                                />
                            </FormGrid>
                            <FormGrid>
                                <Label htmlFor={ScimOutboundConfigResponseFields.groupEndpoint}>{i18n.connectors.groupEndpoint}</Label>
                                <Input
                                    id={ScimOutboundConfigResponseFields.groupEndpoint}
                                    value={form.groupEndpoint}
                                    onChange={(e) => updateForm({groupEndpoint: e.target.value})}
                                />
                            </FormGrid>
                        </DetailGrid>
                    </FormGrid>
                </Stack>

                {/* Auth + HTTP Fields (shared) */}
                <ConnectorConfigForm
                    form={form}
                    onChange={updateForm}
                    mode={mode}
                />

                {/* Features Section */}
                <Stack>
                    <Strong>{i18n.connectors.features}</Strong>
                    <DetailGrid columns={2} responsive={false}>
                        <InlineRow justify="between">
                            <Label htmlFor={ScimOutboundConfigResponseFields.supportsBulk}>{i18n.connectors.supportsBulk}</Label>
                            <Switch
                                id={ScimOutboundConfigResponseFields.supportsBulk}
                                checked={form.supportsBulk}
                                onCheckedChange={(checked) => updateForm({supportsBulk: checked})}
                            />
                        </InlineRow>
                        <InlineRow justify="between">
                            <Label htmlFor={ScimOutboundConfigResponseFields.supportsEtag}>{i18n.connectors.supportsEtag}</Label>
                            <Switch
                                id={ScimOutboundConfigResponseFields.supportsEtag}
                                checked={form.supportsEtag}
                                onCheckedChange={(checked) => updateForm({supportsEtag: checked})}
                            />
                        </InlineRow>
                    </DetailGrid>
                    <FormGrid>
                        <Label htmlFor="patchStyle">{i18n.connectors.patchStyle}</Label>
                        <Select
                            value={form.patchStyle}
                            onValueChange={(value) => updateForm({patchStyle: value})}
                        >
                            <SelectTrigger data-testid="select-patch-style">
                                <SelectValue/>
                            </SelectTrigger>
                            <SelectContent>
                                {PATCH_STYLES.map(({value, labelKey}) => (
                                    <SelectItem key={value} value={value}>{i18n.common[labelKey]}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </FormGrid>
                </Stack>
            </div>
            <SaveCancelFooter
                testIdPrefix={mode === 'create' ? 'create-scim' : 'edit-scim'}
                onCancel={() => mode === 'create' ? createDialog.close() : editDialog.close()}
                saving={mode === 'create' ? createMutation.isPending : updateMutation.isPending}
                disabled={!form.baseUrl}
                saveLabel={mode === 'create' ? i18n.connectors.saveConfiguration : i18n.common.saveChanges}
            />
        </form>
        )
    }

    const getAuthTypeLabel = (type: AuthType) => {
        switch (type) {
            case AuthType.BEARER_TOKEN: return i18n.connectors.bearerToken
            case AuthType.OAUTH2_CLIENT: return i18n.connectors.oauth2Client
            case AuthType.BASIC_AUTH: return i18n.connectors.basicAuth
            case AuthType.TOKEN: return i18n.connectors.tokenAuth
            case AuthType.CERTIFICATE: return i18n.connectors.certificateAuth
            case AuthType.KUBECONFIG: return i18n.connectors.kubeconfigAuth
            case AuthType.NONE: return i18n.connectors.noneAuth
            default: return type
        }
    }

    return (
        <>
            <Card>
                <RowCardHeader>
                    <div>
                        <CardTitle className="items-center gap-2" data-testid="card-title-scim-config">
                            <Settings className="h-5 w-5"/>
                            {i18n.connectors.scimConfiguration}
                        </CardTitle>
                        <CardDescription>{i18n.connectors.scimDescription}</CardDescription>
                    </div>
                    {canUpdate && (
                        scimConfig ? (
                            <Button data-testid="btn-edit-scim-config" size="sm" onClick={openEdit}>
                                <Icon icon={Edit} className="mr-2"/>
                                {i18n.common.edit}
                            </Button>
                        ) : (
                            <Button data-testid="btn-create-scim-config" size="sm" onClick={openCreate}>
                                <Icon icon={Settings} className="mr-2"/>
                                {i18n.connectors.configure}
                            </Button>
                        )
                    )}
                </RowCardHeader>
                <CardContent>
                    {scimConfig ? (
                        <dl className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            <div className="lg:col-span-3">
                                <dt className="font-medium text-muted-foreground">{i18n.connectors.baseUrl}</dt>
                                <dd><CodeText>{scimConfig.baseUrl}</CodeText></dd>
                            </div>
                            <div>
                                <dt className="font-medium text-muted-foreground">{i18n.connectors.userEndpoint}</dt>
                                <dd><CodeText>{scimConfig.userEndpoint}</CodeText></dd>
                            </div>
                            <div>
                                <dt className="font-medium text-muted-foreground">{i18n.connectors.groupEndpoint}</dt>
                                <dd><CodeText>{scimConfig.groupEndpoint}</CodeText></dd>
                            </div>
                            <div>
                                <dt className="font-medium text-muted-foreground">{i18n.connectors.authentication}</dt>
                                <dd className="mt-1">{getAuthTypeLabel(scimConfig.authType)}</dd>
                            </div>
                            <div>
                                <dt className="font-medium text-muted-foreground">{i18n.connectors.connectTimeout}</dt>
                                <dd className="mt-1">{i18n.common.valueWithMs({value: scimConfig.connectTimeoutMs})}</dd>
                            </div>
                            <div>
                                <dt className="font-medium text-muted-foreground">{i18n.connectors.readTimeout}</dt>
                                <dd className="mt-1">{i18n.common.valueWithMs({value: scimConfig.readTimeoutMs})}</dd>
                            </div>
                            <div>
                                <dt className="font-medium text-muted-foreground">{i18n.connectors.maxRetries}</dt>
                                <dd className="mt-1">{scimConfig.maxRetries}</dd>
                            </div>
                            <div>
                                <dt className="font-medium text-muted-foreground">{i18n.connectors.supportsBulk}</dt>
                                <dd className="mt-1 flex items-center gap-1">
                                    {scimConfig.supportsBulk
                                        ? <><Check className="h-4 w-4 text-status-success"/><span>{i18n.common.yes}</span></>
                                        : <><Icon icon={X} muted/><span>{i18n.common.no}</span></>}
                                </dd>
                            </div>
                            <div>
                                <dt className="font-medium text-muted-foreground">{i18n.connectors.supportsEtag}</dt>
                                <dd className="mt-1 flex items-center gap-1">
                                    {scimConfig.supportsEtag
                                        ? <><Check className="h-4 w-4 text-status-success"/><span>{i18n.common.yes}</span></>
                                        : <><Icon icon={X} muted/><span>{i18n.common.no}</span></>}
                                </dd>
                            </div>
                            <div>
                                <dt className="font-medium text-muted-foreground">{i18n.connectors.patchStyle}</dt>
                                <dd className="mt-1">{scimConfig.patchStyle}</dd>
                            </div>
                        </dl>
                    ) : (
                        <MutedText block centered className="py-4">{i18n.connectors.scimNotConfigured}</MutedText>
                    )}
                </CardContent>
            </Card>

            {/* Create Dialog */}
            <Dialog open={createDialog.isOpen} onOpenChange={createDialog.bind.onOpenChange}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{i18n.connectors.configureScimConnection}</DialogTitle>
                        <DialogDescription>{i18n.connectors.configureScimDescription}</DialogDescription>
                    </DialogHeader>
                    {dialogContent('create')}
                </DialogContent>
            </Dialog>

            {/* Edit Dialog */}
            <Dialog open={editDialog.isOpen} onOpenChange={editDialog.bind.onOpenChange}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{i18n.connectors.editScimConfiguration}</DialogTitle>
                        <DialogDescription>{i18n.connectors.editScimDescription}</DialogDescription>
                    </DialogHeader>
                    {dialogContent('edit')}
                </DialogContent>
            </Dialog>
        </>
    )
}
